import { useState, useEffect, useCallback } from 'react';
import { documentTemplateService } from '../services/documentTemplateService';
import {
  DocumentTemplate,
  DocumentTemplateFilters,
  DocumentTemplateUploadData,
  DocumentTemplateStats,
} from '../types/documentTemplate';

interface UseDocumentTemplatesState {
  templates: DocumentTemplate[];
  loading: boolean;
  error: string | null;
  total: number;
  page: number;
  totalPages: number;
  limit: number;
  stats?: DocumentTemplateStats;
}

interface UseDocumentTemplatesActions {
  loadTemplates: (filters?: DocumentTemplateFilters) => Promise<void>;
  createTemplate: (data: DocumentTemplateUploadData) => Promise<void>;
  updateTemplate: (id: string, data: Partial<DocumentTemplate>) => Promise<void>;
  deleteTemplate: (id: string) => Promise<void>;
  downloadTemplate: (id: string, fileName?: string) => Promise<void>;
  uploadNewVersion: (id: string, file: File, version: string) => Promise<void>;
  setPage: (page: number) => void;
  setFilters: (filters: DocumentTemplateFilters) => void;
  refreshTemplates: () => Promise<void>;
}

export interface UseDocumentTemplatesReturn extends UseDocumentTemplatesState, UseDocumentTemplatesActions {}

export function useDocumentTemplates(initialFilters?: DocumentTemplateFilters): UseDocumentTemplatesReturn {
  const [state, setState] = useState<UseDocumentTemplatesState>({
    templates: [],
    loading: false,
    error: null,
    total: 0,
    page: 1,
    totalPages: 0,
    limit: 10,
  });

  const [filters, setCurrentFilters] = useState<DocumentTemplateFilters>(initialFilters || {
    page: 1,
    limit: 10,
  });

  const loadTemplates = useCallback(async (newFilters?: DocumentTemplateFilters) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const filtersToUse = newFilters || filters;
      const response = await documentTemplateService.getTemplates(filtersToUse);
      
      setState(prev => ({
        ...prev,
        templates: response.templates,
        total: response.pagination.total,
        page: response.pagination.page,
        totalPages: response.pagination.totalPages,
        limit: response.pagination.limit,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load templates',
        loading: false,
      }));
    }
  }, [filters]);

  const createTemplate = useCallback(async (data: DocumentTemplateUploadData) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await documentTemplateService.createTemplate(data);
      await loadTemplates(); // Refresh the list
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to create template',
        loading: false,
      }));
      throw error;
    }
  }, [loadTemplates]);

  const updateTemplate = useCallback(async (id: string, data: Partial<DocumentTemplate>) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await documentTemplateService.updateTemplate(id, data);
      
      // Optimistically update the local state
      setState(prev => ({
        ...prev,
        templates: prev.templates.map(template =>
          template.id === id ? { ...template, ...data } : template
        ),
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update template',
        loading: false,
      }));
      throw error;
    }
  }, []);

  const deleteTemplate = useCallback(async (id: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await documentTemplateService.deleteTemplate(id);
      
      // Optimistically remove from local state
      setState(prev => ({
        ...prev,
        templates: prev.templates.filter(template => template.id !== id),
        total: prev.total - 1,
        loading: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to delete template',
        loading: false,
      }));
      throw error;
    }
  }, []);

  const downloadTemplate = useCallback(async (id: string, fileName?: string) => {
    try {
      await documentTemplateService.downloadTemplate(id, fileName);
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to download template',
      }));
      throw error;
    }
  }, []);

  const uploadNewVersion = useCallback(async (id: string, file: File, version: string) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      await documentTemplateService.uploadNewVersion(id, file, version);
      await loadTemplates(); // Refresh to get updated template with new version
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to upload new version',
        loading: false,
      }));
      throw error;
    }
  }, [loadTemplates]);

  const setPage = useCallback((page: number) => {
    const newFilters = { ...filters, page };
    setCurrentFilters(newFilters);
    loadTemplates(newFilters);
  }, [filters, loadTemplates]);

  const setFilters = useCallback((newFilters: DocumentTemplateFilters) => {
    // Only reset to page 1 if page is not explicitly provided (e.g., when changing search/status filters)
    const mergedFilters = { 
      ...filters, 
      ...newFilters, 
      page: newFilters.page !== undefined ? newFilters.page : 1 
    };
    setCurrentFilters(mergedFilters);
    loadTemplates(mergedFilters);
  }, [filters, loadTemplates]);

  const refreshTemplates = useCallback(() => {
    return loadTemplates(filters);
  }, [loadTemplates, filters]);

  // Load templates on mount and when filters change
  useEffect(() => {
    loadTemplates();
  }, []); // Only run on mount, filters are handled by the action functions

  return {
    ...state,
    loadTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    downloadTemplate,
    uploadNewVersion,
    setPage,
    setFilters,
    refreshTemplates,
  };
} 